import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit, inject, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ModalLineOaMemberComponent } from '../../../modals/line-oa/modal-line-oa-member/modal-line-oa-member.component';
import {
  CityParkingUserLineDto,
  CityParkingUserLineResponse,
  LineOADashboardSummaryDto,
  LineOARecentActivityDto,
} from '../../../../../core/models/lineOA.model';
import { ApiLineOaService } from '../../../../../core/services/api/line-oa/api-line-oa.service';
import { MapperLineOaService } from '../../../../../core/mappers/line-oa/mapper-line-oa.service';
import { ToastService } from 'angular-toastify';
@Component({
  selector: 'app-line-oa-member',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalLineOaMemberComponent],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './line-oa-member.component.html',
  styleUrl: './line-oa-member.component.css',
  providers: [DatePipe]
})
export class LineOaMemberComponent implements OnInit {
  private readonly apiLineOaService = inject(ApiLineOaService);
  private readonly mapper = inject(MapperLineOaService);
  private readonly datePipe = inject(DatePipe);
  private readonly toast = inject(ToastService);

  protected Math = Math;

  // --- Search & Sort State ---
  searchText: string = '';
  searchType: 'name' | 'lineName' | 'phone' = 'name';
  isSearchTypeDropdownOpen: boolean = false;
  languageFilter: string = 'all';
  isLangDropdownOpen: boolean = false;
  sortColumn: string = '';
  sortDirection: 'asc' | 'desc' | '' = '';

  // --- Pagination State ---
  currentPage: number = 1;
  pageSize: number = 20;
  pageSizeOptions: number[] = [20, 50, 100];
  totalItems: number = 0;
  totalPages: number = 0;

  // --- Modal State ---
  isModalOpen: boolean = false;
  selectedMember: CityParkingUserLineResponse | null = null;
  activeTab: 'info' | 'license' | 'card' = 'info';

  /** Member ids whose pictureUrl failed to load (show broken-image placeholder). */
  private brokenPictureIds = new Set<number>();

  // --- Data ---
  members: CityParkingUserLineResponse[] = [];
  isLoading: boolean = false;
  lastFetchedTime: Date | null = null;

  // --- Dashboard Data ---
  showDashboard: boolean = true;
  showRatioChart: boolean = true;
  dashboardStats: any[] = [];
  chartData: any[] = [];
  recentActivity: any[] = [];
  newPercent: number = 0;
  oldPercent: number = 0;

  toggleDashboard() {
    this.showDashboard = !this.showDashboard;
  }

  // Chart State
  dashboardData: CityParkingUserLineResponse[] = [];
  chartMode: 'daily' | 'monthly' = 'daily';
  dailySubMode: 'default' | 'custom' = 'default';
  chartType: 'line' | 'bar' = 'line';
  chartWidth: string = '100%';

  customStartDate: string = '';
  customEndDate: string = '';

  selectedMonthStart: string = new Date().toISOString().slice(0, 7);
  selectedMonthEnd: string = new Date().toISOString().slice(0, 7);

  yAxisLabels: number[] = [];
  chartTotalCount: number = 0;

  // Chart Visuals
  currentMonthPoints: string = '';
  currentMonthAreaPoints: string = '';
  prevMonthPoints: string = '';
  currentMonthDots: any[] = [];

  ngOnInit(): void {
    // Check if we arrived via Drag & Drop Sidebar Navigation
    const pendingSearch = sessionStorage.getItem('pendingLineOaSearch');
    if (pendingSearch) {
      try {
        const parsed = JSON.parse(pendingSearch);
        if (parsed.phone) {
          this.searchType = 'phone';
          this.searchText = parsed.phone;
        } else if (parsed.name) {
          this.searchType = 'lineName';
          this.searchText = parsed.name;
        }
        sessionStorage.removeItem('pendingLineOaSearch');
        setTimeout(() => {
          this.toast.info(`รับข้อมูลและค้นหา: ${this.searchText}`);
        }, 500);
      } catch (e) { }
    }

    this.loadMembers();
  }

  formatDate(date: Date): string {
    return this.datePipe.transform(date, 'yyyy-MM-dd') || '';
  }

  /* -------------------------------------- Function -------------------------------------- */

  onRefresh() {
    this.searchText = '';
    this.searchType = 'name';
    this.languageFilter = 'all';
    this.sortColumn = '';
    this.sortDirection = '';
    this.currentPage = 1;
    this.pageSize = 20;
    this.loadMembers();
  }

  // -------------- Drag & Drop Search --------------
  isDraggingOver: boolean = false;

  onDragOver(event: DragEvent) {
    event.preventDefault(); // Necessary to allow dropping
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
    this.isDraggingOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    this.isDraggingOver = false;

    if (event.dataTransfer) {
      const data = event.dataTransfer.getData('application/json');
      if (data) {
        try {
          const parsed = JSON.parse(data);
          if (parsed.type === 'LINE_MEMBER_SEARCH') {

            // Apply search
            if (parsed.phone) {
              this.searchType = 'phone';
              this.searchText = parsed.phone;
            } else if (parsed.name) {
              this.searchType = 'lineName';
              this.searchText = parsed.name;
            }

            this.onClickSearch();
            this.toast.info(`รับข้อมูลและค้นหา: ${this.searchText}`);
          }
        } catch (e) {
          console.error('Invalid drop data', e);
        }
      }
    }
  }

  // -------------- Search --------------
  /** Search only when user clicks Search or presses Enter */
  onClickSearch() {
    this.currentPage = 1;
    this.loadMembers();
  }

  toggleLangDropdown() {
    this.isLangDropdownOpen = !this.isLangDropdownOpen;
    this.isSearchTypeDropdownOpen = false;
  }

  selectLanguage(lang: string) {
    this.languageFilter = lang;
    this.isLangDropdownOpen = false;
    this.currentPage = 1;
    this.loadMembers();
  }

  toggleSearchTypeDropdown() {
    this.isSearchTypeDropdownOpen = !this.isSearchTypeDropdownOpen;
    this.isLangDropdownOpen = false;
  }

  selectSearchType(type: 'name' | 'lineName' | 'phone') {
    this.searchType = type;
    this.isSearchTypeDropdownOpen = false;
  }

  get currentSearchTypeLabel(): string {
    switch (this.searchType) {
      case 'name': return 'ชื่อ-นามสกุล';
      case 'lineName': return 'ชื่อ Line';
      case 'phone': return 'เบอร์โทรศัพท์';
      default: return 'ค้นหา';
    }
  }

  // --------------- Table Logic ---------------
  sortData(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.currentPage = 1;
    this.loadMembers();
  }

  setPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadMembers();
    }
  }

  onPageSizeChange() {
    this.currentPage = 1;
    this.loadMembers();
  }

  get startItemIndex(): number {
    if (this.totalItems === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalItems);
  }

  get pagesArray() {
    const total = this.totalPages;
    const maxVisibleButtons = 5;
    let startPage = Math.max(1, this.currentPage - 2);
    let endPage = Math.min(total, startPage + maxVisibleButtons - 1);
    if (endPage - startPage < maxVisibleButtons - 1) {
      startPage = Math.max(1, endPage - maxVisibleButtons + 1);
    }
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  }

  // --- Modal Functions ---
  isPictureBroken(memberId: number): boolean {
    return this.brokenPictureIds.has(memberId);
  }

  onPictureError(memberId: number): void {
    this.brokenPictureIds.add(memberId);
  }

  openModal(member: CityParkingUserLineResponse) {
    this.selectedMember = member;
    this.activeTab = 'info';
    this.isModalOpen = true;
  }

  closeModal() {
    this.isModalOpen = false;
    this.selectedMember = null;
  }

  /* -------------------------------------- API -------------------------------------- */
  loadMembers(): void {
    this.isLoading = true;
    const searchField = this.searchText.trim()
      ? this.mapper.mapSearchField(this.searchType)
      : undefined;
    const searchText = this.searchText.trim() || undefined;

    this.apiLineOaService.getUserLineOAForTable({
      page: this.currentPage,
      pageSize: this.pageSize,
      searchField,
      searchText,
      language: this.languageFilter,
      sortColumn: this.sortColumn ? this.mapper.mapSortColumn(this.sortColumn) : undefined,
      sortDirection: this.sortDirection || undefined,
    }).subscribe({
      next: (res) => {
        this.brokenPictureIds.clear();
        this.members = (res.data ?? []).map(d => this.mapper.mapUserLineFromDto(d));
        this.totalItems = res.total ?? 0;
        this.totalPages = res.totalPages ?? 0;

        if (res.summary && res.chartMembers) {
          this.applyDashboardFromResponse(res.summary, res.recentActivity ?? [], res.chartMembers);
        }

        this.lastFetchedTime = new Date();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('API error:', error);
        this.toast.error('ไม่สามารถโหลดข้อมูลสมาชิก Line OA ได้');
        this.isLoading = false;
      },
    });
  }

  private applyDashboardFromResponse(
    summary: LineOADashboardSummaryDto,
    recentActivity: LineOARecentActivityDto[],
    chartMembers: CityParkingUserLineDto[],
  ): void {
    const mapped = this.mapper.mapDashboardStats(summary, recentActivity, chartMembers);
    this.dashboardStats = mapped.dashboardStats;
    this.newPercent = mapped.newPercent;
    this.oldPercent = mapped.oldPercent;
    this.recentActivity = mapped.recentActivity.map(item => ({
      initial: item.initial,
      name: item.name,
      timeAgo: this.getTimeAgo(item.createDate ?? null),
      info: item.info,
      color: this.getRandomColor(item.id),
    }));
    this.dashboardData = mapped.chartMembers;
    this.processDashboardData();
  }

  /* -------------------------------------- Chart & Controls -------------------------------------- */

  setChartMode(mode: 'daily' | 'monthly') {
    this.chartMode = mode;
    if (mode === 'monthly') {
      // Reset monthly range to current month on switch if desired, or keep logic
      const today = new Date();
      const currentMonthStub = today.toISOString().slice(0, 7);
      const prevMonthDate = new Date(today.getFullYear(), today.getMonth() - 1, 1); // Default to show last 2 months context or similar?
      // RegisterParking sets prevMonth -> currentMonth
      this.selectedMonthStart = new Date(today.getFullYear(), today.getMonth() - 5, 1).toISOString().slice(0, 7); // Show 6 months by default? Or 2?
      // Let's default to prev month to current month to match context
      this.selectedMonthStart = prevMonthDate.toISOString().slice(0, 7);
      this.selectedMonthEnd = currentMonthStub;
      this.onChartMonthChange();
      return;
    }
    this.processDashboardData();
  }

  setDailySubMode(mode: 'default' | 'custom') {
    this.dailySubMode = mode;
    if (mode === 'custom') {
      if (!this.customStartDate) {
        const s = new Date();
        s.setDate(s.getDate() - 6);
        this.customStartDate = this.formatDate(s);
      }
      if (!this.customEndDate) {
        const s = new Date(this.customStartDate);
        const e = new Date(this.customStartDate);
        e.setDate(s.getDate() + 6);
        this.customEndDate = this.formatDate(e);
      }
      this.onCustomDateChange();
    } else {
      this.processDashboardData();
    }
  }

  setChartType(type: 'line' | 'bar') {
    this.chartType = type;
  }

  get maxMonthEnd(): string {
    if (!this.selectedMonthStart) return '';
    const start = new Date(this.selectedMonthStart + '-01');
    const maxDate = new Date(start);
    maxDate.setMonth(maxDate.getMonth() + 11);
    return maxDate.toISOString().slice(0, 7);
  }

  onChartMonthChange() {
    if (this.chartMode !== 'monthly') return;

    // Validate 12-month limit
    if (this.selectedMonthStart && this.selectedMonthEnd) {
      const start = new Date(this.selectedMonthStart + '-01');
      const end = new Date(this.selectedMonthEnd + '-01');

      // Calculate month difference
      const diffMonths = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());

      if (diffMonths > 11) {
        // Exceeds 12 months span (start + 11 more months)
        const newEnd = new Date(start);
        newEnd.setMonth(start.getMonth() + 11);
        this.selectedMonthEnd = newEnd.toISOString().slice(0, 7);
      } else if (end < start) {
        // Handle negative range
        this.selectedMonthEnd = this.selectedMonthStart;
      }
    }

    this.processDashboardData();
  }

  onCustomDateChange() {
    if (!this.customStartDate || !this.customEndDate) return;
    const s = new Date(this.customStartDate);
    const e = new Date(this.customEndDate);
    if (e < s) this.customEndDate = this.customStartDate;

    // Max 10 days constraint check
    const diffDays = Math.ceil((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays > 10) {
      // Warn? We don't have ToastService injected here.
      // Just auto-fix or ignore for now.
      // We should inject ToastService if we want consistency.
      // For now, auto-fix silently.
      const newEnd = new Date(s);
      newEnd.setDate(s.getDate() + 9);
      this.customEndDate = this.formatDate(newEnd);
    }
    this.processDashboardData();
  }

  get maxCustomDate(): string {
    if (!this.customStartDate) return '';
    const d = new Date(this.customStartDate);
    d.setDate(d.getDate() + 10);
    return this.formatDate(d);
  }

  resetCustomDate() {
    const e = new Date();
    const s = new Date();
    s.setDate(e.getDate() - 9);
    this.customEndDate = this.formatDate(e);
    this.customStartDate = this.formatDate(s);
    this.processDashboardData();
  }

  processDashboardData() {
    // 2. Chart Rendering (Using this.dashboardData - Chart Filter)
    let xLabels: any[] = [];
    let dataPoints: number[] = [];
    let diffDays = 0;

    if (this.chartMode === 'daily') {
      let start: Date;
      let end: Date;
      if (this.dailySubMode === 'custom' && this.customStartDate && this.customEndDate) {
        start = new Date(this.customStartDate);
        end = new Date(this.customEndDate);
      } else {
        start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        end = new Date();
      }

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        xLabels.push(new Date(d));
        dataPoints.push(0);
      }

      this.dashboardData.forEach(m => {
        if (!m.createDate) return;
        const d = new Date(m.createDate);
        const dDateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

        if (dDateOnly >= startDateOnly && dDateOnly <= endDateOnly) {
          const idx = xLabels.findIndex(x => x.toDateString() === dDateOnly.toDateString());
          if (idx !== -1) dataPoints[idx]++;
        }
      });

    } else {
      // Monthly
      const [startYear, startMonth] = this.selectedMonthStart.split('-').map(Number);
      const [endYear, endMonth] = this.selectedMonthEnd.split('-').map(Number);
      const start = new Date(startYear, startMonth - 1, 1);
      const end = new Date(endYear, endMonth, 0);

      // Use Daily Granularity if <= 60 days
      const diffTime = Math.abs(end.getTime() - start.getTime());
      diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 60) {
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          xLabels.push(new Date(d));
          dataPoints.push(0);
        }
        this.dashboardData.forEach(m => {
          if (!m.createDate) return;
          const d = new Date(m.createDate);
          if (d >= start && d <= end) {
            const idx = xLabels.findIndex(x => x.toDateString() === d.toDateString());
            if (idx !== -1) dataPoints[idx]++;
          }
        });
      } else {
        // Monthly Aggregation for longer ranges
        let temp = new Date(startYear, startMonth - 1, 1);
        while (temp <= end) {
          xLabels.push(new Date(temp));
          dataPoints.push(0);
          temp.setMonth(temp.getMonth() + 1);
        }
        this.dashboardData.forEach(m => {
          if (!m.createDate) return;
          const d = new Date(m.createDate);
          if (d < start || d > end) return;
          const idx = xLabels.findIndex(x => x.getFullYear() === d.getFullYear() && x.getMonth() === d.getMonth());
          if (idx !== -1) dataPoints[idx]++;
        });
      }
    }

    this.chartTotalCount = dataPoints.reduce((a, b) => a + b, 0);

    // Scaling & Points
    const rawMax = Math.max(...dataPoints, 5);
    const maxVal = Math.ceil(rawMax / 4) * 4;
    this.yAxisLabels = [0, maxVal * 0.25, maxVal * 0.5, maxVal * 0.75, maxVal];

    const getCoordinate = (val: number, index: number, total: number) => {
      if (total <= 1) return { x: 50, y: 100 - ((val / maxVal) * 100) };
      // Match RegisterParking: Add padding (2% - 98%)
      const x = 2 + (index / (total - 1)) * 96;
      const y = 100 - ((val / maxVal) * 100);
      return { x, y };
    };

    const pointsObj = dataPoints.map((val, i) => getCoordinate(val, i, dataPoints.length));
    this.currentMonthPoints = pointsObj.map(p => `${p.x},${p.y}`).join(' ');

    const getThaiMonth = (monthIndex: number) => {
      const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
      return months[monthIndex];
    };

    this.currentMonthDots = pointsObj.map((p, i) => {
      const date = xLabels[i];
      let label = '';
      let subLabel = '';

      const day = date.getDate();
      label = `${day}`; // Always show day
      if (day === 1 || i === 0) {
        subLabel = getThaiMonth(date.getMonth());
      }

      // If monthly aggregation (xLabel is 1st of month)
      if (this.chartMode === 'monthly' && dataPoints.length <= 12 && diffDays > 60) {
        label = getThaiMonth(date.getMonth());
        subLabel = `${date.getFullYear() + 543}`.slice(2);
      }

      return {
        x: p.x,
        y: p.y,
        label: label,
        subLabel: subLabel,
        value: dataPoints[i],
        color: this.getChartColor(i)
      };
    });



    if (pointsObj.length > 0) {
      this.currentMonthAreaPoints = `
          ${pointsObj[0].x},100
          ${this.currentMonthPoints}
          ${pointsObj[pointsObj.length - 1].x},100
       `;
    } else {
      this.currentMonthAreaPoints = '';
    }

    // Dynamic Width Calculation for Scrolling
    // Threshold: If more than 30 points (approx 1 month), start scrolling
    // width per point: ~40px to be readable
    if (dataPoints.length > 30) {
      this.chartWidth = `${dataPoints.length * 40}px`;
    } else {
      this.chartWidth = '100%';
    }
  }
  getRandomColor(id: number) {
    const colors = ['bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
    return colors[id % colors.length];
  }
  getChartColor(index: number) {
    const colors = ['#fbbf24', '#f59e0b', '#34d399', '#60a5fa', '#f87171', '#818cf8', '#a78bfa', '#f472b6'];
    return colors[index % colors.length];
  }

  // Remove Helper Helper that are duplicates if any... keeping just in case.

  /* -------------------------------------- Helper -------------------------------------- */
  getTimeAgo(dateInput: string | Date | null): string {
    if (!dateInput) return '-';
    const date = new Date(dateInput);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    // ... same as before
    let interval = seconds / 31536000;
    if (interval > 1) return Math.floor(interval) + ' ปีที่แล้ว';
    interval = seconds / 2592000;
    if (interval > 1) return Math.floor(interval) + ' เดือนที่แล้ว';
    interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + ' วันที่แล้ว';
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + ' ชั่วโมงที่แล้ว';
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + ' นาทีที่แล้ว';
    return 'เพิ่งสร้าง';
  }

  clearSearch(): void {
    if (!this.searchText) return;
    this.searchText = '';
    this.onClickSearch();
  }
}
